/**
 * Shared password rules for Signup and Login (same API contract as backend).
 * Order matters: first failing rule drives the error message.
 */

export const API_PASSWORD_REGEX = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/;

/**
 * @param {string} usernamePart — email local-part, phone digits, or login username (trimmed)
 */
export function getPasswordRuleMeta(usernamePart) {
  const up = (usernamePart || "").trim();
  return [
    {
      id: "notAllNumbers",
      label: "Cannot be all numbers",
      passes: (p) => p.length > 0 && !/^\d+$/.test(p),
      error: "Password cannot be only numbers.",
    },
    {
      id: "notAllLetters",
      label: "Cannot be all letters (case-sensitive)",
      passes: (p) => p.length > 0 && !/^[a-zA-Z]+$/.test(p),
      error: "Password cannot be only letters.",
    },
    {
      id: "minLength",
      label: "Minimum 8 characters required",
      passes: (p) => p.length >= 8,
      error: "Password must be at least 8 characters.",
    },
    {
      id: "notContainsUsername",
      label: "Cannot contain username",
      passes: (p) =>
        !up ||
        up.length < 2 ||
        !p.toLowerCase().includes(up.toLowerCase()),
      error: "Password cannot contain your email username or phone number.",
    },
    {
      id: "complexity",
      label: "Uppercase, lowercase, number, and a special character (#?!@$%^&*-)",
      passes: (p) => API_PASSWORD_REGEX.test(p),
      error: "Use at least 8 characters with uppercase, lowercase, a number, and a special character (#?!@$%^&*-).",
    },
  ];
}

/** @returns {('idle' | 'ok' | 'bad' | 'pending')[]} */
export function getPasswordRuleRowStates(password, rules) {
  const p = String(password || "");
  if (!p.trim()) {
    return rules.map(() => "idle");
  }
  const results = rules.map((r) => r.passes(p));
  const firstFail = results.findIndex((ok) => !ok);
  if (firstFail === -1) {
    return rules.map(() => "ok");
  }
  return rules.map((_, i) => {
    if (i < firstFail) return "ok";
    if (i === firstFail) return "bad";
    return "pending";
  });
}

/**
 * @param {string} password
 * @param {ReturnType<getPasswordRuleMeta>} rules
 * @returns {{ ok: true } | { ok: false, message: string }}
 */
export function validatePasswordAgainstRules(password, rules) {
  const p = String(password || "");
  if (!p.trim()) {
    return { ok: false, message: "Please enter your password" };
  }
  for (let i = 0; i < rules.length; i += 1) {
    if (!rules[i].passes(p)) {
      return { ok: false, message: rules[i].error };
    }
  }
  return { ok: true };
}
